import * as PIXI from "pixi.js";

/**
 * A ticker-based replacement for setTimeout that auto-pauses when the tab is hidden.
 *
 * Uses PIXI.Ticker.shared to accumulate elapsed time per frame. When the browser
 * tab loses focus, requestAnimationFrame stops firing → the ticker stops → all
 * TickerTimeouts freeze in place. When the tab regains focus, they resume from
 * exactly where they were — zero restoration code needed.
 *
 * Usage:
 *   const tt = new TickerTimeout(() => { doSomething(); }, 500);
 *   tt.clear(); // cancel before it fires
 *
 * Drop-in replacement for setTimeout in game timing logic.
 */
class TickerTimeout {
  /**
   * @param {Function} callback - Function to call when delay elapses
   * @param {number} delay - Delay in milliseconds
   */
  constructor(callback, delay) {
    this.callback = callback;
    this.delay = delay;
    this.elapsed = 0;
    this.active = true;

    this._tickerFn = () => {
      if (!this.active) return;
      this.elapsed += PIXI.Ticker.shared.deltaMS;
      if (this.elapsed >= this.delay) {
        this.active = false;
        PIXI.Ticker.shared.remove(this._tickerFn);
        this.callback();
      }
    };

    PIXI.Ticker.shared.add(this._tickerFn);
  }

  /**
   * Cancel this timeout. Safe to call multiple times.
   */
  clear() {
    if (!this.active) return;
    this.active = false;
    PIXI.Ticker.shared.remove(this._tickerFn);
  }
}

export default TickerTimeout;
